import Box from "@/ui/atoms/Box";
import Typography from "@/ui/atoms/Typography";
import type {
    TChartsOfAccountsTab,
    TDashboardTab,
} from "@/ui/router/routerPaths";
import { createCoreUILinks, createProdaLinks } from "@/utils/createProdaLink";
import {
    Dropdown,
    DropdownSection,
    DropdownSelect,
    DropdownSelectItem,
    SidebarItem,
    useNavigationSidebarContext,
} from "@proda-ai/ui-kit";
import { useEffect, useRef } from "react";

export type ViewSettingsMenuParams = {
    useState: () => {
        settingsOpen: boolean;
        isLoading: boolean;
        isAuthenticated: boolean;
        user: { email?: string; updated_at?: string } | null;
        isProdaUser: boolean;
        isAdmin: boolean;
    };
    useCases: () => {
        handleSettingsClicked: () => void;
        syncButtonClicked: () => Promise<void>;
        logout: () => void;
        chartsOfAccountsButtonClicked: (tab: TChartsOfAccountsTab) => void;
        dashboardButtonClicked: (tab: TDashboardTab) => void;
        userPoliciesButtonClicked: () => void;
    };
};

export const createViewSettingsMenu = ({
    useState,
    useCases,
}: ViewSettingsMenuParams) => {
    const ViewSettingsMenu = () => {
        const {
            settingsOpen,
            isAuthenticated,
            isLoading,
            user,
            isProdaUser,
            isAdmin,
        } = useState();
        const {
            handleSettingsClicked,
            syncButtonClicked,
            logout,
            chartsOfAccountsButtonClicked,
            userPoliciesButtonClicked,
            dashboardButtonClicked,
        } = useCases();
        const { isSidebarExpanded } = useNavigationSidebarContext();
        const dropdownTargetRef = useRef<HTMLDivElement>(null);
        const buttonRef = useRef<HTMLDivElement>(null);

        const userLoginInfo = () => {
            const lastLogin = new Date(user?.updated_at ?? "");
            const dateOptions = {
                year: "numeric",
                month: "short",
                day: "numeric",
            } as const;
            return (
                <Box
                    sx={{
                        gap: "0.25rem",
                        backgroundColor: "var(--proda-white-130)",
                        cursor: "pointer",
                        borderTopRightRadius: "0.5rem",
                        borderTopLeftRadius: "0.5rem",
                    }}
                    onClick={handleSettingsClicked}
                >
                    {isLoading && (
                        <Typography variant="body4">Loading...</Typography>
                    )}
                    {isAuthenticated && (
                        <Box
                            sx={{
                                padding: "0.34375rem 0.5rem",
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <Typography variant="body3">
                                <em>{user?.email}</em>
                            </Typography>
                            <Typography variant="body3">
                                <em>
                                    Last Login:{" "}
                                    {lastLogin.toLocaleDateString(
                                        "en-GB",
                                        dateOptions,
                                    )}{" "}
                                    {lastLogin.toLocaleTimeString("en-GB", {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                    })}
                                </em>
                            </Typography>
                        </Box>
                    )}
                </Box>
            );
        };

        const getDropdownPosition = () => {
            if (!buttonRef.current) return {};

            const rect = buttonRef.current.getBoundingClientRect();
            return {
                top: `${rect.top}px`,
                left:
                    isSidebarExpanded === true
                        ? `${rect.right - 200}px`
                        : `${rect.left}px`,
                transform: `translateY(-100%) translateY(-8px)`,
            };
        };

        useEffect(() => {
            const handleKeydown = (event: KeyboardEvent) => {
                if (event.key === "Escape" && settingsOpen) {
                    handleSettingsClicked();
                }
            };
            document.addEventListener("keydown", handleKeydown);
            return () => document.removeEventListener("keydown", handleKeydown);
        }, [settingsOpen, handleSettingsClicked]);

        useEffect(() => {
            const handleClickOutside = (event: MouseEvent) => {
                if (
                    settingsOpen &&
                    dropdownTargetRef.current &&
                    buttonRef.current &&
                    !dropdownTargetRef.current.contains(event.target as Node) &&
                    !buttonRef.current.contains(event.target as Node)
                ) {
                    handleSettingsClicked();
                }
            };
            if (settingsOpen) {
                document.addEventListener("mousedown", handleClickOutside);
            }
            return () =>
                document.removeEventListener("mousedown", handleClickOutside);
        }, [settingsOpen, handleSettingsClicked]);

        return (
            <Box ref={buttonRef}>
                <Dropdown
                    isDismissible
                    isShowing={settingsOpen}
                    onItemSelected={handleSettingsClicked}
                    dropdownRenderElement={dropdownTargetRef.current}
                    onClose={handleSettingsClicked}
                >
                    <SidebarItem
                        icon="gear"
                        label="Settings"
                        onClick={handleSettingsClicked}
                        data-testid="settings-icon"
                        current={settingsOpen}
                    />
                    {settingsOpen && (
                        <Box
                            sx={{
                                backgroundColor: "var(--proda-white)",
                                borderRadius: "0.5rem",
                                boxShadow:
                                    "0px 0px 10px 0px rgba(0, 0, 0, 0.1)",
                                position: "fixed",
                                zIndex: 1000,
                                minWidth: "200px",
                                ...getDropdownPosition(),
                            }}
                            ref={dropdownTargetRef}
                        >
                            <DropdownSelect
                                currentIndex={-1}
                                labelledById="options"
                                role="listbox"
                            >
                                {userLoginInfo()}
                                {isProdaUser && (
                                    <DropdownSelectItem
                                        label="Admin (new)"
                                        onClick={() => {
                                            window.location.href =
                                                createCoreUILinks("/admin");
                                        }}
                                    />
                                )}
                                <DropdownSection
                                    label="Rent Rolls"
                                    color="proda-black-10"
                                />
                                <DropdownSelectItem
                                    label="Settings"
                                    onClick={() => {
                                        window.location.href =
                                            createProdaLinks("/settings");
                                    }}
                                />
                                <DropdownSelectItem
                                    label="Downloads"
                                    onClick={() => {
                                        window.location.href =
                                            createProdaLinks("/your-downloads");
                                    }}
                                />
                                <DropdownSelectItem
                                    label="EUR Exchange Rates"
                                    onClick={() => {
                                        window.location.href = createProdaLinks(
                                            "/company/exchange-rates",
                                        );
                                    }}
                                />
                                {isAdmin && (
                                    <DropdownSelectItem
                                        label="Admin Panel"
                                        onClick={() => {
                                            window.location.href =
                                                createProdaLinks("/manage");
                                        }}
                                    />
                                )}
                                <DropdownSection
                                    label="Operating Statements"
                                    color="proda-black-10"
                                />
                                <DropdownSelectItem
                                    role="option"
                                    label="Sync"
                                    onClick={syncButtonClicked}
                                />
                                {isAdmin && (
                                    <DropdownSelectItem
                                        label="Charts of Accounts"
                                        onClick={() =>
                                            chartsOfAccountsButtonClicked(
                                                "list",
                                            )
                                        }
                                    />
                                )}
                                {isProdaUser && (
                                    <DropdownSelectItem
                                        label="Dashboard"
                                        onClick={() =>
                                            dashboardButtonClicked("deal")
                                        }
                                    />
                                )}
                                <DropdownSection
                                    label="User Policies"
                                    color="proda-black-10"
                                />
                                <DropdownSelectItem
                                    label="Terms & Conditions"
                                    onClick={userPoliciesButtonClicked}
                                />
                                <DropdownSelectItem
                                    label="Logout"
                                    onClick={logout}
                                />
                            </DropdownSelect>
                        </Box>
                    )}
                </Dropdown>
            </Box>
        );
    };
    return ViewSettingsMenu;
};
